import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Grid,
  LinearProgress,
  IconButton,
  Tooltip,
  Alert,
} from '@mui/material';
import {
  Send as SendIcon,
  Person as PersonIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { CompanyLayout } from '../../components/Layout';
import { useScoutMessage } from '../../hooks/useScoutMessage';
import { SendScoutMessageRequest } from '../../types/scout-message';

interface LocationState {
  selectedStudent?: {
    id: number;
    full_name: string;
    university?: string;
    faculty?: string;
  };
}

const ScoutComposerPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState;

  const { isLoading, error, sendMessage, quota, loadQuota } = useScoutMessage();

  // 学生が選択されていない場合は学生検索画面にリダイレクト
  useEffect(() => {
    if (!state?.selectedStudent) {
      navigate('/company/students/search', {
        replace: true,
        state: {
          message: 'スカウトを送信する学生を選択してください',
        },
      });
    }
  }, [state, navigate]);

  // Form state
  const [recipient, setRecipient] = useState(state?.selectedStudent || null);
  const [subject, setSubject] = useState('');
  const [messageBody, setMessageBody] = useState('');

  // Load initial data
  useEffect(() => {
    loadQuota();
  }, [loadQuota]);

  const isValid = useMemo(() => {
    const subjectTrimmed = subject.trim();
    const messageTrimmed = messageBody.trim();

    return (
      recipient &&
      subjectTrimmed.length > 0 &&
      subject.length <= 50 &&
      messageTrimmed.length > 0 &&
      messageBody.length <= 1000 &&
      (quota?.data?.currentUsage?.perDay?.used || 0) <
        (quota?.data?.currentUsage?.perDay?.limit || 50)
    );
  }, [recipient, subject, messageBody, quota]);

  const handleSend = async () => {
    if (!isValid || !recipient) return;

    const trimmedSubject = subject.trim();
    const trimmedMessageBody = messageBody.trim();

    const request: SendScoutMessageRequest = {
      recipientUserId: recipient.id,
      subject: trimmedSubject,
      messageBody: trimmedMessageBody,
      sendImmediately: true,
    };

    const success = await sendMessage(request);
    if (success) {
      navigate('/company/scout/history');
    }
  };

  return (
    <CompanyLayout>
      <Container maxWidth="xl">
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h4"
            sx={{
              mb: 1,
              color: '#111827',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <EditIcon sx={{ mr: 1 }} />
            スカウトメッセージ作成
          </Typography>
          <Typography variant="body2" color="text.secondary">
            学生に向けたスカウトメッセージを作成・送信します
          </Typography>
        </Box>

        {error && (
          <Alert
            severity="error"
            sx={{ mb: 3 }}
            onClose={() => (window as any).setError?.(null)}
          >
            {error}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* メイン作成エリア */}
          <Grid item xs={12} lg={8}>
            <Paper sx={{ p: 3 }}>
              {/* 送信先学生 */}
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="h6"
                  sx={{ mb: 2, display: 'flex', alignItems: 'center' }}
                >
                  <PersonIcon sx={{ mr: 1 }} />
                  送信先学生
                </Typography>

                {recipient && (
                  <Card variant="outlined">
                    <CardContent
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Box>
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 'bold' }}
                        >
                          {recipient.full_name}
                        </Typography>
                        {recipient.university && (
                          <Typography variant="body2" color="text.secondary">
                            {recipient.university} {recipient.faculty}
                          </Typography>
                        )}
                      </Box>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => navigate('/company/students/search')}
                        sx={{ ml: 2 }}
                      >
                        別の学生を選択
                      </Button>
                    </CardContent>
                  </Card>
                )}
              </Box>

              {/* 件名入力 */}
              <Box sx={{ mb: 3 }}>
                <TextField
                  fullWidth
                  label="件名"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="魅力的な件名を入力してください"
                  inputProps={{ maxLength: 50 }}
                  helperText={`${subject.length}/50文字`}
                  error={subject.length > 50}
                />
              </Box>

              {/* メッセージ本文 */}
              <Box sx={{ mb: 3 }}>
                <TextField
                  fullWidth
                  multiline
                  rows={12}
                  label="メッセージ本文"
                  value={messageBody}
                  onChange={(e) => setMessageBody(e.target.value)}
                  placeholder="学生への具体的で丁寧なメッセージを入力してください"
                  inputProps={{ maxLength: 1000 }}
                  helperText={`${messageBody.length}/1000文字`}
                  error={messageBody.length > 1000}
                />
              </Box>

              {/* 送信ボタン */}
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate(-1)}
                  disabled={isLoading}
                >
                  キャンセル
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSend}
                  disabled={!isValid || isLoading}
                  startIcon={<SendIcon />}
                >
                  {isLoading ? '送信中...' : 'スカウト送信'}
                </Button>
              </Box>
            </Paper>
          </Grid>

          {/* サイドバー */}
          <Grid item xs={12} lg={4}>
            {/* 送信制限情報 */}
            {quota && (
              <Paper sx={{ p: 3 }}>
                <Typography
                  variant="h6"
                  sx={{ mb: 2, display: 'flex', alignItems: 'center' }}
                >
                  <InfoIcon sx={{ mr: 1 }} />
                  送信制限
                  <Tooltip title="制限情報を更新">
                    <IconButton size="small" onClick={loadQuota} sx={{ ml: 1 }}>
                      <RefreshIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      今日の送信数
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Typography variant="body1">
                        {quota?.data?.currentUsage?.perDay?.used || 0} /{' '}
                        {quota?.data?.currentUsage?.perDay?.limit || 50}
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={
                          ((quota?.data?.currentUsage?.perDay?.used || 0) /
                            (quota?.data?.currentUsage?.perDay?.limit || 50)) *
                          100
                        }
                        sx={{ width: 60 }}
                      />
                    </Box>
                  </Box>

                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      今月の送信数
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Typography variant="body1">
                        {quota?.data?.currentUsage?.perMonth?.used || 0} /{' '}
                        {quota?.data?.currentUsage?.perMonth?.limit || 1000}
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={
                          ((quota?.data?.currentUsage?.perMonth?.used || 0) /
                            (quota?.data?.currentUsage?.perMonth?.limit ||
                              1000)) *
                          100
                        }
                        sx={{ width: 60 }}
                      />
                    </Box>
                  </Box>
                </Box>

                {quota?.data?.recommendations &&
                  quota.data.recommendations.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 1 }}
                      >
                        推奨事項
                      </Typography>
                      {quota.data.recommendations.map(
                        (rec: string, index: number) => (
                          <Typography
                            key={index}
                            variant="caption"
                            sx={{ display: 'block', mb: 0.5 }}
                          >
                            • {rec}
                          </Typography>
                        )
                      )}
                    </Box>
                  )}
              </Paper>
            )}
          </Grid>
        </Grid>
      </Container>
    </CompanyLayout>
  );
};

export default ScoutComposerPage;
